import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// Generic direct-upload drop zone (TASK-81.20): the csv-upload pattern with
// the accepted extensions and size limit as values. First consumer is the
// floorplan upload modal; csv-upload remains as-is (consolidation is a
// TASK-82 candidate).
export default class extends Controller {
  static targets = [
    "fileInput",
    "dropZone",
    "progressContainer",
    "progressBar",
    "progressText",
    "fileName",
    "submitButton",
    "blobId",
    "errorMessage"
  ]

  static values = {
    directUploadUrl: String,
    // Lowercase extensions, e.g. ["pdf", "png", "jpg", "jpeg", "webp"]
    extensions: Array,
    maxSizeMb: { type: Number, default: 20 },
    kindLabel: { type: String, default: "file" },
    // TASK-158.5. OFF by default, which is what keeps the other seven zones
    // that use this controller — floorplans, site imports, certification
    // provider logos, submission fields, avatars — behaving exactly as they
    // did: one file, named, submitted with the form around it. Files turns it
    // on, and a selection of several is handed to the upload queue instead,
    // because naming a batch is not a thing anyone wants to do.
    multiple: Boolean
  }

  connect() {
    this.boundDragOver = this.dragOver.bind(this)
    this.boundDragLeave = this.dragLeave.bind(this)
    this.boundDrop = this.drop.bind(this)

    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.addEventListener("dragover", this.boundDragOver)
      this.dropZoneTarget.addEventListener("dragleave", this.boundDragLeave)
      this.dropZoneTarget.addEventListener("drop", this.boundDrop)
    }
  }

  disconnect() {
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.removeEventListener("dragover", this.boundDragOver)
      this.dropZoneTarget.removeEventListener("dragleave", this.boundDragLeave)
      this.dropZoneTarget.removeEventListener("drop", this.boundDrop)
    }
  }

  dragOver(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.add("border-blue-500", "bg-blue-50")
  }

  dragLeave(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove("border-blue-500", "bg-blue-50")
  }

  drop(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove("border-blue-500", "bg-blue-50")

    if (this.handOffBatch(event.dataTransfer.files)) return

    const file = event.dataTransfer.files[0]
    if (file) {
      this.processFile(file)
    }
  }

  selectFile() {
    this.fileInputTarget.click()
  }

  handleFileSelected(event) {
    if (this.handOffBatch(event.target.files)) return

    const file = event.target.files[0]
    if (file) {
      this.processFile(file)
    }
  }

  // Several files at once are somebody else's problem: this zone is a form
  // field with one name, one progress bar and one submit button. Where a
  // batch is meaningful the page says so with multiple-value and listens for
  // the event; everywhere else this returns false and nothing changes.
  handOffBatch(fileList) {
    if (!this.multipleValue || !fileList || fileList.length < 2) return false

    this.dispatch("enqueue", { detail: { files: Array.from(fileList) } })
    return true
  }

  processFile(file) {
    const allowed = this.extensionsValue.map((e) => e.toLowerCase())
    const extension = file.name.split(".").pop().toLowerCase()
    if (allowed.length > 0 && !allowed.includes(extension)) {
      this.showError(`Please select a ${allowed.map((e) => e.toUpperCase()).join(", ")} ${this.kindLabelValue}.`)
      return
    }

    if (file.size > this.maxSizeMbValue * 1024 * 1024) {
      this.showError(`File is too large. Maximum size is ${this.maxSizeMbValue}MB.`)
      return
    }

    this.clearError()
    this.fileNameTarget.textContent = file.name
    this.fileNameTarget.classList.remove("hidden")
    this.progressContainerTarget.classList.remove("hidden")
    this.submitButtonTarget.disabled = true

    const upload = new DirectUpload(file, this.directUploadUrlValue, {
      directUploadWillStoreFileWithXHR: (request) => {
        request.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100)
            this.progressBarTarget.style.width = `${percent}%`
            this.progressTextTarget.textContent = `${percent}%`
          }
        })
      }
    })

    upload.create((error, blob) => {
      if (error) {
        this.showError("Upload failed. Please try again.")
        this.progressContainerTarget.classList.add("hidden")
        return
      }

      this.blobIdTarget.value = blob.signed_id
      this.submitButtonTarget.disabled = false
      this.progressTextTarget.textContent = "Upload complete"
    })
  }

  showError(message) {
    this.errorMessageTarget.textContent = message
    this.errorMessageTarget.classList.remove("hidden")
  }

  clearError() {
    this.errorMessageTarget.textContent = ""
    this.errorMessageTarget.classList.add("hidden")
  }
}
